import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ImageSquareIcon, TagIcon, XIcon } from "@phosphor-icons/react";

const MAX_IMAGES = 4;
const MAX_TEXT = 1500;

/**
 * Composer for creating a new seller post.
 *
 * Always renders 3 inputs:
 * - Caption (text up to 1500 chars)
 * - Up to 4 images (drag-or-pick, instant local preview)
 * - Optional listing attachment (paginated picker of seller's own listings)
 *
 * Submits via multipart `/api/posts/upload` (so we don't need a separate
 * upload-then-attach round-trip).
 */
export default function ComposePostModal({
  open,
  onOpenChange,
  prefilledListingId = null,
  prefilledListing = null,
  onPosted,
}) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]); // [{ file, previewUrl }]
  const [listingId, setListingId] = useState(prefilledListingId);
  const [listing, setListing] = useState(prefilledListing);
  const [myListings, setMyListings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset state on close so reopening starts clean.
      setText("");
      setFiles((prev) => {
        prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
        return [];
      });
      setListingId(prefilledListingId);
      setListing(prefilledListing);
      setPickerOpen(false);
    }
    // We deliberately reset only on close.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Lazy-load the seller's own listings for the picker.
  useEffect(() => {
    if (!pickerOpen || myListings.length > 0) return;
    (async () => {
      try {
        const me = await api.get("/auth/me");
        const res = await api.get(`/listings?seller_id=${me.data.user_id}&limit=50`);
        setMyListings(res.data.items || []);
      } catch (e) {
        toast.error(apiError(e));
      }
    })();
  }, [pickerOpen, myListings.length]);

  const onPickFiles = (fileList) => {
    const incoming = Array.from(fileList || []).slice(0, MAX_IMAGES - files.length);
    if (!incoming.length) return;
    const enriched = incoming
      .filter((f) => f.type.startsWith("image/"))
      .map((file) => ({ file, previewUrl: URL.createObjectURL(file) }));
    setFiles((prev) => [...prev, ...enriched].slice(0, MAX_IMAGES));
  };

  const removeFile = (idx) => {
    setFiles((prev) => {
      const next = [...prev];
      const [removed] = next.splice(idx, 1);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  };

  const submit = async () => {
    const t = text.trim();
    if (!t && files.length === 0 && !listingId) {
      toast.error("Add a caption, an image, or a listing.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      if (t) fd.append("text", t);
      if (listingId) fd.append("listing_id", listingId);
      files.forEach((f) => fd.append("files", f.file));
      const res = await api.post("/posts/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Posted to your followers' feed");
      onPosted?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remainingSlots = MAX_IMAGES - files.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="compose-post-dialog">
        <DialogHeader>
          <DialogTitle>New post</DialogTitle>
          <DialogDescription>
            Share an update with everyone who follows you. Posts appear in
            their feed and on your public profile.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="What's the drop, hype, or story behind this listing?"
            rows={4}
            maxLength={MAX_TEXT}
            className="text-sm resize-none"
            data-testid="compose-post-text"
            autoFocus
          />
          <div className="text-[11px] text-zinc-400 text-right font-mono -mt-2">
            {text.length}/{MAX_TEXT}
          </div>

          {/* Image grid */}
          {files.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              {files.map((f, i) => (
                <div
                  key={i}
                  className="relative aspect-square rounded-lg overflow-hidden border border-zinc-200 bg-zinc-50"
                >
                  <img
                    src={f.previewUrl}
                    alt={`upload-${i}`}
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white inline-flex items-center justify-center hover:bg-black/80"
                    data-testid={`compose-post-remove-image-${i}`}
                    aria-label="Remove image"
                  >
                    <XIcon size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Attached listing card */}
          {listing && (
            <div className="flex items-center gap-3 p-2.5 rounded-xl border border-zinc-200 bg-zinc-50">
              <div className="h-12 w-12 rounded-md bg-zinc-200 overflow-hidden flex-shrink-0">
                {listing.images?.[0] ? (
                  <img
                    src={listing.images[0]}
                    alt={listing.title}
                    className="h-full w-full object-cover"
                  />
                ) : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate">{listing.title}</div>
                <div className="font-mono text-[#1D78AB] text-xs font-bold">
                  ${(listing.price || 0).toFixed(2)}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => {
                  setListingId(null);
                  setListing(null);
                }}
                data-testid="compose-post-detach-listing"
              >
                <XIcon size={14} />
              </Button>
            </div>
          )}

          {/* Action row */}
          <div className="flex items-center gap-2 pt-1">
            <label
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold rounded-full border cursor-pointer transition ${
                remainingSlots > 0
                  ? "border-zinc-200 hover:border-[#1D78AB] hover:text-[#1D78AB]"
                  : "border-zinc-100 text-zinc-300 cursor-not-allowed"
              }`}
              data-testid="compose-post-image-btn"
            >
              <ImageSquareIcon size={14} />
              {remainingSlots > 0 ? `Add image (${remainingSlots} left)` : "Max images"}
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => onPickFiles(e.target.files)}
                disabled={remainingSlots <= 0}
              />
            </label>
            {!listingId && (
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold rounded-full border border-zinc-200 hover:border-[#1D78AB] hover:text-[#1D78AB]"
                data-testid="compose-post-attach-listing"
              >
                <TagIcon size={14} /> Attach listing
              </button>
            )}
          </div>

          {/* Inline listing picker */}
          {pickerOpen && (
            <div className="border border-zinc-200 rounded-lg max-h-56 overflow-y-auto bg-white">
              {myListings.length === 0 ? (
                <div className="p-3 text-xs text-zinc-500 text-center">
                  Loading your listings…
                </div>
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {myListings.map((l) => (
                    <li
                      key={l.id}
                      className="p-2.5 hover:bg-zinc-50 cursor-pointer flex items-center gap-2.5"
                      onClick={() => {
                        setListingId(l.id);
                        setListing(l);
                        setPickerOpen(false);
                      }}
                      data-testid={`compose-post-pick-${l.id}`}
                    >
                      <div className="h-9 w-9 bg-zinc-200 rounded-md overflow-hidden flex-shrink-0">
                        {l.images?.[0] ? (
                          <img
                            src={l.images[0]}
                            alt={l.title}
                            className="h-full w-full object-cover"
                          />
                        ) : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{l.title}</div>
                        <div className="text-[11px] text-zinc-500 font-mono">
                          ${(l.price || 0).toFixed(2)} · {l.status}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="compose-post-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || (!text.trim() && files.length === 0 && !listingId)}
            className="bg-[#1D78AB] hover:bg-[#176089] text-white"
            data-testid="compose-post-submit"
          >
            {busy ? "Posting…" : "Post"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
